// Hands-free live voice conversation (VOICE_AI_DESIGN — Phase 1 "live mode"). Drives the
// continuous loop that makes Breww talk back-and-forth like ChatGPT/Claude voice:
//
//   listening → (you speak, auto-detect silence) → transcribing → thinking → speaking → …
//
// No typing, no send button. This design was chosen by an adversarial design panel (3
// proposals → 3 judges → synthesis). The decisions worth knowing:
//
//  • REPLY CAPTURE is EVENT-DRIVEN, not `await send()`. A useEffect keyed on
//    [streaming, active, lastAssistant] fires on the exact React commit where the final
//    streamed reply is live — `lastAssistant` is a committed prop in that dep array — so
//    the reply text is NEVER read stale. (Awaiting send() then reading a ref races the
//    passive-effect commit; the effect sidesteps it, exactly like the existing
//    Speak-Replies effect.) A watchdog covers the rare case where send() early-returns
//    without ever streaming, so 'thinking' can't hang.
//  • A monotonic runId is bumped on every enter AND teardown; every deferred continuation
//    (rAF tick, recorder.onstop, transcribe, watchdog, speak latch) re-checks it, so an
//    exit/restart makes all in-flight work inert — no races, no leaks.
//  • ONE MediaStream + AudioContext for the whole session; a fresh MediaRecorder per
//    utterance. Silence detection is a dependency-free Web Audio AnalyserNode with
//    ambient-floor calibration + two-threshold hysteresis + onset debounce (no CDN → CSP
//    is never an issue).
//  • Speaking exits through a single settled-latch, because stopSpeaking() emits no 'ended'
//    event — so a manual interrupt plus a late natural-end can't double-advance.
//  • Cost brakes: pure-silence clips never hit the network; N consecutive empty listens
//    auto-exit the mode; a server 429 surfaces as an error, not a silent stall.

import { useCallback, useEffect, useRef, useState } from 'react';
import { stopSpeaking, stopAllAudio, transcribeBlob, pickAudioMime, cleanForSpeech, createSpeechQueue, VOICE_MODE_INSTRUCTIONS, buildDeliveryInstructions, type SpeechQueue } from './voice';
import { createVoiceCapture, type VoiceCapture } from './voice-capture';

// P1A: continuous AudioWorklet capture with a pre-roll ring buffer (no first-word loss, barge-in
// in every phase). Falls back to the MediaRecorder-per-utterance path if the worklet is
// unavailable OR when this flag is off.
// TEMPORARILY OFF: on a real mic the engine's end-of-turn didn't fire reliably (graph-pull +
// stale noise-floor). The MediaRecorder path (relative endpointing) is the known-good endpoint.
// Both fixes are applied in voice-capture.ts; re-enable after real-device verification.
const CONTINUOUS_CAPTURE = false;

/**
 * Pull COMPLETE sentences out of a growing streamed reply. Returns the finished sentences
 * beyond `from` and how far we consumed, so the caller can speak each sentence the instant
 * it's ready (low latency) rather than waiting for the whole reply. A sentence ends at
 * .!?… followed by whitespace, or a newline; `flush` (stream finished) also emits the tail.
 * A soft cap breaks a very long run so the first audio never waits too long.
 */
function takeSentences(text: string, from: number, flush: boolean, earlyBreak = false): { sentences: string[]; consumed: number } {
  const out: string[] = [];
  const n = text.length;
  let start = from;
  let i = from;
  // `earlyBreak` (first chunk only): break at the first clause boundary so Breww starts
  // speaking the opening phrase within a beat of the first tokens instead of a full sentence.
  const SOFT_CAP = earlyBreak ? 70 : 180;
  const GROUP_MIN = 100; // B7: after the opener, fold sentences together until a group is ~this long
  const isEnd = (idx: number): boolean => {
    const c = text[idx];
    if (c === '\n') return true;
    if (c === '.' || c === '!' || c === '?' || c === '…') {
      const next = text[idx + 1];
      return next === undefined ? flush : /\s/.test(next);
    }
    return false;
  };
  const isClause = (idx: number): boolean => {
    const c = text[idx];
    if (c !== ',' && c !== ';' && c !== ':') return false;
    const next = text[idx + 1];
    return next === undefined ? flush : /\s/.test(next);
  };
  while (i < n) {
    if (isEnd(i)) {
      // B7: after the opener, fold short sentences together so each TTS synth gets ~2–3 sentences
      // → smoother cross-sentence prosody and fewer calls. Keep scanning past this sentence end
      // until the group reaches GROUP_MIN or the stream flushes (SOFT_CAP below still caps a very
      // long run). The opener (earlyBreak) always emits immediately so first audio stays fast.
      if (!earlyBreak && !flush && i - start < GROUP_MIN) { i++; continue; }
      let end = i + 1;
      while (end < n && /\s/.test(text[end] as string)) end++;
      const seg = text.slice(start, end).trim();
      if (seg) out.push(seg);
      start = end; i = end; continue;
    }
    // First-chunk clause break: after ~24 chars, end on the first comma/semicolon/colon.
    if (earlyBreak && out.length === 0 && i - start >= 24 && isClause(i)) {
      let end = i + 1;
      while (end < n && /\s/.test(text[end] as string)) end++;
      const seg = text.slice(start, end).trim();
      if (seg) out.push(seg);
      start = end; i = end; continue;
    }
    if (i - start >= SOFT_CAP) {
      let br = text.lastIndexOf(' ', i);
      if (br <= start) br = i;
      const seg = text.slice(start, br).trim();
      if (seg) out.push(seg);
      start = br; while (start < n && /\s/.test(text[start] as string)) start++;
      i = start; continue;
    }
    i++;
  }
  if (flush && start < n) {
    const seg = text.slice(start).trim();
    if (seg) out.push(seg);
    start = n;
  }
  return { sentences: out, consumed: start };
}

export type VoicePhase =
  | 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking' | 'error' | 'muted';

export interface VoiceConversationArgs {
  /** The mode is open. Flip true to start the loop, false to tear everything down. */
  active: boolean;
  /** Chat's in-flight flag — true while a reply streams, false when it settles. */
  streaming: boolean;
  /** The last finalized assistant message. Committed prop → the reply-capture effect
   *  reads it on the exact commit where the streamed reply is live. */
  lastAssistant: { id: string; content: string } | null;
  /** Send a user turn (Chat.tsx `send`). Fire-and-forget — we do NOT await it. */
  onSend: (text: string) => void;
  /** Optional: stop the current turn (Chat.tsx stop) — barge-in while thinking. */
  onStop?: (() => void) | undefined;
  /** Optional: close the mode from inside (the empty-listen cost brake). */
  onAutoExit?: (() => void) | undefined;
  /** Optional (B1): on barge-in, rewrite the last assistant message to ONLY what was actually
   *  spoken, so the model's memory matches what the user heard (no repeat / desync next turn). */
  onTruncate?: ((spokenText: string) => void) | undefined;
}

export interface VoiceConversationState {
  phase: VoicePhase;
  /** Your last recognized utterance — shown as a caption. */
  transcript: string;
  /** 0..1 smoothed mic level — drives the orb's reactive scale. */
  level: number;
  /** Human-readable error (mic permission / no device / daily limit) in the 'error' phase. */
  error: string | null;
  /** True while the mic is muted (hard-disabled — no capture, no barge-in). */
  muted: boolean;
  /** True once your voice has been detected in the current listen (drives feedback copy). */
  heard: boolean;
  /** 0..1 — how close the end-of-turn timer is (drives the countdown ring). */
  endpointProgress: number;
  /** Barge-in / skip: stop the current step and listen again now. */
  interrupt: () => void;
  /** End your turn NOW and send it (tap-to-submit / "Done") — instant, no waiting for silence. */
  submitTurn: () => void;
  /** Toggle the mic mute (disables the track; the AI can still finish speaking). */
  toggleMute: () => void;
  /** Recover from the error phase (re-request the mic). */
  retry: () => void;
}

// ── VAD + loop tuning (ms unless noted). RMS is on a normalized [-1,1] signal. ──
const CALIBRATION_MS = 500;       // sample ambient noise this long (median/percentile floor)
const VOICE_ONSET_MS = 180;       // energy must sustain THIS long to count as the user talking —
                                  // raised from 150 so brief background noises (a thud, a click, a
                                  // far-off word) don't register as your speech (noiseSuppression
                                  // is also on). Real speech clears it instantly; no word is lost
                                  // (the recorder captures from the top of the listen).
const MIN_SPEECH_MS = 250;        // ignore sub-quarter-second blips
// ADAPTIVE endpointing — resolves "cuts me off" vs "keeps listening after I finish". The
// end-of-turn silence widens for long monologues / proven pausers; a manual Done/tap ends it
// instantly. Detection is RELATIVE (see REL_DROP).
// NOTE: base was briefly 450ms (a latency tweak) but real use showed that clips a natural
// mid-thought pause and sends STT a fragment ("slight delay → wrong answer"). 700ms is the
// industry-normal end-of-turn wait (ChatGPT/Gemini voice sit ~700–800ms), so this is the RIGHT
// default, not a compromise; the LLM's ASR-repair prompt catches whatever still slips through.
const SILENCE_HOLD_MS = 700;      // base end-of-turn hold — tolerant of a slight thinking pause
const LONG_TURN_MS = 4000;        // only a genuinely long monologue earns extra patience
const HOLD_LONG_MS = 1000;        // …this much end-silence patience once it's long
const MID_PAUSE_MS = 350;         // a dip below the dynamic threshold this long = a real pause
                                  // (lower ⇒ a pauser is recognised sooner, so grace kicks in faster)
const HOLD_PER_PAUSE_MS = 250;    // +grace per proven pause-then-resume (a habitual pauser)
const HOLD_MAX_MS = 1400;         // ceiling on the adaptive hold (a heavy pauser gets up to this)
// RELATIVE end-of-turn threshold: the silence line sits this fraction of the way up from the
// measured noise floor to YOUR speaking level, so it detects the DROP when you stop regardless
// of the room's absolute loudness — no hardcoded silence number, robust with AGC off.
const REL_DROP = 0.35;
const NO_SPEECH_MS = 8000;        // nothing said this long → re-arm without sending
const MAX_UTTERANCE_MS = 30000;   // hard cap (was 15s — chopped real answers mid-word)
const MIN_UTTERANCE_BYTES = 400;  // truly-empty blob guard (VAD verdict, not bytes, gates real speech)
const REPLY_WATCHDOG_MS = 4000;   // send() never streamed → don't hang in "thinking"
const MAX_EMPTY_EXIT = 6;         // this many empty listens in a row → auto-exit (cost brake)
// NOTE: RMS here is on a normalized [-1,1] mic signal — normal speech sits ~0.02–0.10 on a
// typical laptop mic, so these are deliberately SENSITIVE (a quiet voice must still register).
const ONSET_FALLBACK = 0.018;     // onset threshold before calibration completes
const ONSET_MULT = 2.0;           // onset  = clamp(noiseFloor * this, 0.010 … ONSET_CAP)
const SILENCE_MULT = 1.3;         // silence = clamp(noiseFloor * this, 0.006 … SILENCE_CAP)  (< onset ⇒ hysteresis)
const ONSET_CAP = 0.050;          // cap so a loud/noisy room can't threshold a real voice out
const SILENCE_CAP = 0.030;        // "" — keeps end-of-speech from firing mid-sentence
const ONSET_FLOOR = 0.010;        // never demand LESS than this (rejects fan/hum)
const SILENCE_FLOOR = 0.006;

// STT-accuracy A/B toggle. Browser noiseSuppression tames steady background noise, but it can
// also gate/distort quiet speech ONSETS before STT ever sees them (a cause of misheard first
// words). Flip to false to test cleaner onsets in quiet rooms; echoCancellation stays on (barge-in
// needs it). Kept true by default so noisy rooms don't regress before a real-device A/B.
const STT_NOISE_SUPPRESSION = true;

// B8 (prosody proxy): mirror the user's vibe in the REPLY's delivery. We reuse two signals the
// VAD already computes — peak speaking energy and mid-thought pause count — to build a one-line
// steer ("they sounded animated/subdued/hesitant → lean your voice that way"). This is the
// prosody a text cascade otherwise discards; it's a coarse energy/pace cue, NOT full emotion
// recognition (true valence needs an audio model + a wav transcode, or the realtime lane).
const PROSODY_HOT = 0.10;    // peak RMS (normalized) at/above this = animated / energetic
const PROSODY_COLD = 0.035;  // peak RMS at/below this = quiet / subdued

/** One-line delivery-mirroring hint from the just-finished utterance's prosody ('' = neutral). */
function prosodyHint(peak: number, pauseCount: number): string {
  const energy = peak >= PROSODY_HOT ? 'hot' : peak <= PROSODY_COLD ? 'cold' : 'mid';
  const hesitant = pauseCount >= 2;
  if (energy === 'hot') return 'They sounded lively and energetic — let a little of that upbeat energy into your voice.';
  if (energy === 'cold') return hesitant
    ? 'They sounded quiet and a bit hesitant — stay soft, calm and unhurried, and give them room.'
    : 'They sounded quiet and low-key — keep your voice gentle, warm and relaxed, not loud or bubbly.';
  if (hesitant) return 'They spoke slowly, with pauses — keep an easy, patient pace, no rush.';
  return '';
}

// Acoustic barge-in — start talking WHILE Breww THINKS or SPEAKS and it stops + listens, no
// button. Robust against the model's own voice bleeding back: browser echoCancellation is on AND
// the threshold tracks the residual echo CONTINUOUSLY (a running estimate updated from non-voice
// frames), not just a one-shot sample — so it can sit low enough to catch a soft "wait" without
// self-triggering on TTS bleed. Runs across both 'thinking' (no echo → very sensitive) and
// 'speaking' (echo-adaptive), with a fresh grace window re-armed at the thinking→speaking flip.
const ACOUSTIC_BARGE_IN = true;
const BARGE_GRACE_SPEAK_MS = 200; // skip the TTS attack transient when speaking begins
const BARGE_GRACE_THINK_MS = 120; // skip the tail of the user's own just-ended speech
const BARGE_SUSTAIN_MS = 240;     // min sustained voice before it can count as an interruption
const BARGE_CONFIRM_MS = 550;     // sustained THIS long = a real interruption even if quiet (so a
                                  //   brief low-energy "mhm"/"yeah" backchannel does NOT cancel)
const BARGE_ASSERT_RMS = 0.09;    // peak at/above this within the sustain = clearly talking over →
                                  //   cancel at SUSTAIN without waiting for CONFIRM (fast, ~240ms)
const BARGE_RMS_FLOOR = 0.02;     // absolute floor (headphones / thinking → catches a soft "stop")
const BARGE_ECHO_MULT = 2.2;      // threshold = max(floor, runningEcho * this)
const BARGE_ECHO_DECAY = 0.98;    // running echo estimate: rises instantly to a louder bleed frame,
                                  //   decays slowly, so the threshold stays just above TTS residue

export function useVoiceConversation(args: VoiceConversationArgs): VoiceConversationState {
  const { active, streaming, lastAssistant, onSend, onStop, onAutoExit, onTruncate } = args;

  const [phase, setPhase] = useState<VoicePhase>('idle');
  const [transcript, setTranscript] = useState('');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  // Feedback signals: `heard` = your voice has been detected this turn; `endpointProgress`
  // 0..1 = how close the end-of-turn timer is (drives the countdown ring). Quantized so the
  // rAF loop doesn't thrash React state.
  const [heard, setHeard] = useState(false);
  const heardRef = useRef(false);
  const [endpointProgress, setEndpointProgress] = useState(0);
  const epRef = useRef(0);

  // Live mirrors so deferred callbacks read the latest without re-subscribing.
  const activeRef = useRef(active);
  const phaseRef = useRef<VoicePhase>('idle');
  const streamingRef = useRef(streaming);
  const onSendRef = useRef(onSend);
  const onStopRef = useRef(onStop);
  const onAutoExitRef = useRef(onAutoExit);
  const onTruncateRef = useRef(onTruncate);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { onSendRef.current = onSend; }, [onSend]);
  useEffect(() => { onStopRef.current = onStop; }, [onStop]);
  useEffect(() => { onAutoExitRef.current = onAutoExit; }, [onAutoExit]);
  useEffect(() => { onTruncateRef.current = onTruncate; }, [onTruncate]);
  const setPhaseBoth = useCallback((p: VoicePhase) => { phaseRef.current = p; setPhase(p); }, []);

  // Monotonic run id — bumped on every enter AND teardown. Deferred steps bail on mismatch.
  const runIdRef = useRef(0);
  // Persistent audio graph (one per session).
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // Per-utterance capture.
  const recRef = useRef<MediaRecorder | null>(null);
  const rafRef = useRef<number | null>(null);
  const bargeRafRef = useRef<number | null>(null);   // acoustic-barge-in monitor (runs while speaking)
  const stopReasonRef = useRef<'silence' | 'max' | 'nospeech' | 'abort'>('silence');
  // Turn/reply bookkeeping.
  const expectReplyRef = useRef(false);
  const watchdogRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Streaming speech: the reply is spoken sentence-by-sentence as it generates. `spokenUpTo`
  // is how far into the reply text we've already enqueued.
  const speechQueueRef = useRef<SpeechQueue | null>(null);
  const spokenUpToRef = useRef(0);
  const emptyListensRef = useRef(0);
  // A5: per-turn delivery steer. `lastUserTextRef` = this turn's transcript; `turnStyleRef` =
  // the affect-tailored TTS instruction, computed ONCE from the reply (null = not computed yet).
  const lastUserTextRef = useRef('');
  const turnStyleRef = useRef<string | null>(null);
  // B8: latest utterance's prosody (peak energy + pause count), captured by the VAD tick and
  // read when the reply's delivery is built, so the voice can mirror how the user just spoke.
  const speechPeakRef = useRef(0);
  const pauseCountRef = useRef(0);

  // Function refs so the reply-capture effect + event handlers call the latest without
  // widening the effect's dep array (it must key only on [streaming, active, lastAssistant]).
  const startListeningRef = useRef<(my: number) => void>(() => {});
  const finishUtteranceRef = useRef<(my: number, r: 'silence' | 'max' | 'nospeech' | 'abort') => void>(() => {});
  const bargeCancelRef = useRef<(my: number) => void>(() => {});
  // P1A continuous-capture engine (null = legacy MediaRecorder path).
  const captureRef = useRef<VoiceCapture | null>(null);
  const continuousRef = useRef(false);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
  }, []);
  const cancelRaf = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);
  const cancelBargeRaf = useCallback(() => {
    if (bargeRafRef.current !== null) { cancelAnimationFrame(bargeRafRef.current); bargeRafRef.current = null; }
  }, []);

  // Acoustic barge-in monitor: while Breww is THINKING or SPEAKING, watch the mic; if your voice
  // rises above the echo-adaptive threshold and holds, cancel the turn and start listening — the
  // hands-free equivalent of tapping interrupt (B2 covers the whole thinking→speaking window, so
  // you can jump in during the reply's think time, not only after audio starts). The threshold
  // tracks residual TTS echo CONTINUOUSLY (running estimate from non-voice frames) so it stays
  // sensitive without self-triggering; a brief low-energy backchannel ("mhm") is ignored (B4).
  const startBargeMonitor = useCallback((my: number) => {
    if (!ACOUSTIC_BARGE_IN) return;
    cancelBargeRaf();
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    let phaseStart = performance.now();
    let seenPhase = phaseRef.current;
    let echoEst = 0, bargeStart = 0, burstPeak = 0;
    const tick = () => {
      const ph = phaseRef.current;
      if (my !== runIdRef.current || !activeRef.current || (ph !== 'speaking' && ph !== 'thinking')) { bargeRafRef.current = null; return; }
      // Muted = you've chosen to block your mic — Breww keeps talking; never barge while muted.
      if (mutedRef.current) { bargeStart = 0; burstPeak = 0; bargeRafRef.current = requestAnimationFrame(tick); return; }
      const t = performance.now();
      // Re-arm a fresh grace window at the thinking→speaking flip so the TTS attack transient
      // (which lands exactly at speaking onset) can't be mistaken for your voice.
      if (ph !== seenPhase) { seenPhase = ph; phaseStart = t; bargeStart = 0; burstPeak = 0; }
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = ((buf[i] ?? 128) - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const graceMs = ph === 'thinking' ? BARGE_GRACE_THINK_MS : BARGE_GRACE_SPEAK_MS;
      if (t - phaseStart < graceMs) { echoEst = Math.max(echoEst, rms); bargeRafRef.current = requestAnimationFrame(tick); return; }
      const thresh = Math.max(BARGE_RMS_FLOOR, echoEst * BARGE_ECHO_MULT);
      if (rms > thresh) {
        if (!bargeStart) { bargeStart = t; burstPeak = 0; }
        if (rms > burstPeak) burstPeak = rms;
        const dur = t - bargeStart;
        // Fire on a clearly-assertive burst quickly, or on a quiet-but-sustained one — a short,
        // soft backchannel satisfies neither and lets Breww keep talking.
        if (dur >= BARGE_SUSTAIN_MS && (burstPeak >= BARGE_ASSERT_RMS || dur >= BARGE_CONFIRM_MS)) {
          bargeCancelRef.current(my); return; // cancel the LLM too, not just playback
        }
      } else {
        bargeStart = 0; burstPeak = 0;
        echoEst = Math.max(rms, echoEst * BARGE_ECHO_DECAY); // adapt only from non-voice frames
      }
      bargeRafRef.current = requestAnimationFrame(tick);
    };
    bargeRafRef.current = requestAnimationFrame(tick);
  }, [cancelBargeRaf]);

  // Release the whole audio graph. Safe to call repeatedly.
  const releaseAudio = useCallback(() => {
    cancelRaf();
    cancelBargeRaf();
    try { captureRef.current?.stop(); } catch { /* noop */ }
    captureRef.current = null;
    continuousRef.current = false;
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch { /* noop */ }
    recRef.current = null;
    streamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
    streamRef.current = null;
    try { void audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, [cancelRaf, cancelBargeRaf]);

  // Open a fresh streaming speech queue for a turn. The reply-capture effect feeds it
  // sentences as they generate; the queue speaks them in order (pre-fetching the next), so
  // the first sentence plays ~1s into generation instead of after the whole reply. When it
  // drains (after seal), re-arm listening.
  const openSpeechQueue = useCallback((my: number) => {
    speechQueueRef.current?.cancel();
    spokenUpToRef.current = 0;
    turnStyleRef.current = null;        // A5: recomputed from THIS turn's reply
    speechQueueRef.current = createSpeechQueue({
      onFirstAudio: () => {
        if (my !== runIdRef.current || !activeRef.current) return;
        if (phaseRef.current === 'thinking') setPhaseBoth('speaking');
        // The barge monitor is already running from 'thinking' (started in transcribeAndSend) and
        // follows the phase into 'speaking' itself — no (re)start here (B2).
      },
      onDrained: () => { if (my === runIdRef.current && activeRef.current) startListeningRef.current(my); },
      onLimit: () => { if (my === runIdRef.current && activeRef.current) { setError('Daily voice limit reached. Try again later.'); setPhaseBoth('error'); } },
      shouldPlay: () => my === runIdRef.current && activeRef.current,
      // A5: per-turn dynamic delivery — resolved at each synth so the affect locked from this
      // turn's reply applies to every sentence; falls back to the warm base until it's computed.
      instructions: () => turnStyleRef.current ?? VOICE_MODE_INSTRUCTIONS,
    });
  }, [setPhaseBoth]);

  // Transcribe the clip and, on success, send it as a turn (abort-able; 429-aware).
  const transcribeAndSend = useCallback(async (my: number, blob: Blob) => {
    setPhaseBoth('transcribing');
    const ac = new AbortController();
    abortRef.current = ac;
    let text = '';
    let status = 0;
    try {
      ({ text, status } = await transcribeBlob(blob, ac.signal, { fast: true })); // A2: live = Groq-first + short timeout
    } catch (e) {
      if (my !== runIdRef.current || (e as Error).name === 'AbortError') return;
      startListeningRef.current(my); return; // transient STT failure → keep the loop alive
    }
    if (my !== runIdRef.current || !activeRef.current) return;
    if (status === 429) { setError('Daily voice limit reached. Try again later.'); setPhaseBoth('error'); return; }
    if (!text) {
      emptyListensRef.current++;
      if (emptyListensRef.current >= MAX_EMPTY_EXIT) { onAutoExitRef.current?.(); return; }
      startListeningRef.current(my); return;
    }
    emptyListensRef.current = 0;
    setTranscript(text);
    lastUserTextRef.current = text;   // A5: feeds the per-turn delivery steer
    // Never drop into send()'s own `if (streaming) return` guard — the words would vanish with
    // no bubble. After a barge-in we called onStop() to abort the prior turn, but its
    // setStreaming(false) settles a beat later — so WAIT for it to clear rather than DISCARDING
    // what you just said (the reported "I interrupt but it ignores me" bug). The abort→false
    // transition is normally well under a second; the generous window (B5) makes a dropped
    // interruption vanishingly rare short of a genuinely stuck stream.
    for (let waited = 0; streamingRef.current && waited < 3000; waited += 100) {
      await new Promise((r) => setTimeout(r, 100));
      if (my !== runIdRef.current || !activeRef.current) return;
    }
    if (streamingRef.current) { startListeningRef.current(my); return; } // truly stuck → re-arm
    expectReplyRef.current = true;
    openSpeechQueue(my);     // ready to speak sentences the moment they stream in
    setPhaseBoth('thinking');
    if (!continuousRef.current) startBargeMonitor(my); // B2: allow barge-in during think time, not just speaking
    onSendRef.current(text); // Chat sets streaming=true synchronously
    clearWatchdog();
    watchdogRef.current = window.setTimeout(() => {
      if (my === runIdRef.current && phaseRef.current === 'thinking' && !streamingRef.current && expectReplyRef.current) {
        expectReplyRef.current = false;
        startListeningRef.current(my);
      }
    }, REPLY_WATCHDOG_MS);
  }, [setPhaseBoth, clearWatchdog, openSpeechQueue, startBargeMonitor]);

  // Stop the current clip; onstop (below) routes to transcription or re-arms.
  const finishUtterance = useCallback((my: number, reason: 'silence' | 'max' | 'nospeech' | 'abort') => {
    if (my !== runIdRef.current) return;
    cancelRaf();
    stopReasonRef.current = reason;
    const rec = recRef.current;
    if (!rec || rec.state === 'inactive') return;
    try { rec.stop(); } catch { /* onstop still fires in most engines */ }
  }, [cancelRaf]);
  finishUtteranceRef.current = finishUtterance;

  // Record one utterance on the PERSISTENT stream, running the VAD frame loop.
  const startListening = useCallback((my: number) => {
    if (my !== runIdRef.current || !activeRef.current) return;
    clearWatchdog();
    cancelBargeRaf();
    speechQueueRef.current?.cancel();   // never listen over a still-playing/synthesizing reply
    speechQueueRef.current = null;
    stopSpeaking();
    setTranscript('');
    setError(null);
    if (mutedRef.current) { setLevel(0); setPhaseBoth('muted'); return; } // paused — unmute to resume
    // Continuous capture never stops — "re-arm to listen" is just a phase reset (the engine has
    // been buffering the whole time, so the next utterance keeps its pre-roll).
    if (continuousRef.current) { setLevel(0); setPhaseBoth('listening'); return; }
    const stream = streamRef.current;
    const analyser = analyserRef.current;
    if (!stream || !analyser) return;

    const mime = pickAudioMime();
    let rec: MediaRecorder;
    try { rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); }
    catch { setError('Voice capture is not supported in this browser.'); setPhaseBoth('error'); return; }
    recRef.current = rec;
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      if (recRef.current === rec) recRef.current = null;
      const reason = stopReasonRef.current;
      if (my !== runIdRef.current || !activeRef.current || reason === 'abort') return;
      const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
      // Trust the VAD verdict, not raw bytes: reason 'silence'/'max' means it CONFIRMED real
      // speech, so always transcribe (a short real word makes a tiny mp4 blob on Safari — the
      // old byte gate dropped it). Only 'nospeech' (or a truly empty blob) re-arms.
      if (reason === 'nospeech' || blob.size < MIN_UTTERANCE_BYTES) {
        emptyListensRef.current++;
        if (emptyListensRef.current >= MAX_EMPTY_EXIT) { onAutoExitRef.current?.(); return; }
        startListeningRef.current(my); return; // heard nothing → re-arm, no send
      }
      void transcribeAndSend(my, blob);
    };

    // VAD bookkeeping (closure-local, one utterance — resets every startListening).
    let speechStart = 0, hasSpoken = false;
    let lastVoice = performance.now();
    const started = lastVoice;
    let onsetT = 0, silenceT = 0, levelSmooth = 0, noiseFloor = 0, speechPeak = 0;
    const calib: number[] = [];
    // Adaptive-endpointing state: count real mid-thought pauses so a proven pauser earns more
    // end-of-turn patience (and long monologues get more too).
    let pauseCount = 0, inGap = false, gapStart = 0;
    heardRef.current = false; setHeard(false);
    epRef.current = 0; setEndpointProgress(0);
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      const a = analyserRef.current;
      if (my !== runIdRef.current || !activeRef.current || !a) return;
      // A suspended AudioContext feeds the analyser pure silence → nothing is ever heard.
      // Resume it (allowed during the post-tap activation window) so the mic actually flows.
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => { /* noop */ });
      a.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = ((buf[i] ?? 128) - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      levelSmooth = levelSmooth * 0.75 + Math.min(1, rms * 12) * 0.25; // lively, visible orb
      setLevel(levelSmooth);
      const t = performance.now();
      // Ambient calibration → MEDIAN + p90 floor (robust to a stray transient), then two
      // hysteresis thresholds (onset > silence). The p90 term lifts the onset above a genuinely
      // noisy room so it can actually sit ABOVE the floor; the caps bound it so a quiet voice
      // still registers. Only sample plausibly-ambient frames (< 0.05).
      if (!hasSpoken && t - started < CALIBRATION_MS) {
        if (rms < 0.05) calib.push(rms);
      } else if (onsetT === 0) {
        if (calib.length) {
          calib.sort((x, y) => x - y);
          const nf = calib[Math.floor(calib.length * 0.5)] ?? 0;
          const p90 = calib[Math.floor(calib.length * 0.9)] ?? nf;
          noiseFloor = nf;
          onsetT = Math.min(ONSET_CAP, Math.max(ONSET_FLOOR, Math.max(nf * ONSET_MULT, p90 * 1.15)));
          silenceT = Math.min(SILENCE_CAP, Math.max(SILENCE_FLOOR, nf * SILENCE_MULT));
        } else {
          noiseFloor = ONSET_FALLBACK * 0.4;
          onsetT = ONSET_FALLBACK; silenceT = ONSET_FALLBACK * 0.7; // spoke through the whole window
        }
      }
      const onset = onsetT || ONSET_FALLBACK;
      if (rms > onset) {
        if (!speechStart) speechStart = t;
        if (!hasSpoken && t - speechStart >= VOICE_ONSET_MS) hasSpoken = true;
      } else if (!hasSpoken) {
        speechStart = 0; // unconfirmed blip → allow a clean re-trigger
      }
      // RELATIVE end-of-turn detection. `speechPeak` follows your recent speaking level; the
      // silence line is set partway (REL_DROP) between the noise floor and that peak — so it
      // detects the DROP when you stop no matter how loud the room is (the absolute silenceT is
      // just a lower floor). A dip below it for >= MID_PAUSE_MS then recovering counts as a real
      // mid-thought pause and buys extra end-of-turn grace.
      if (hasSpoken) {
        speechPeak = rms > speechPeak ? rms : speechPeak * 0.995 + rms * 0.005;
        const dynSilence = Math.max(silenceT, noiseFloor + REL_DROP * (speechPeak - noiseFloor));
        if (rms >= dynSilence) {
          if (inGap) { if (t - gapStart >= MID_PAUSE_MS) pauseCount = Math.min(pauseCount + 1, 4); inGap = false; }
          lastVoice = t;
        } else if (!inGap) {
          inGap = true; gapStart = t;
        }
        speechPeakRef.current = speechPeak; pauseCountRef.current = pauseCount; // B8: prosody snapshot
      }
      // ADAPTIVE end-of-turn hold: base, widened for long monologues + habitual pausers.
      let hold = SILENCE_HOLD_MS;
      if (hasSpoken && t - speechStart > LONG_TURN_MS) hold = HOLD_LONG_MS;
      hold = Math.min(HOLD_MAX_MS, hold + pauseCount * HOLD_PER_PAUSE_MS);
      const spokeEnough = hasSpoken && t - speechStart > MIN_SPEECH_MS;
      if (hasSpoken !== heardRef.current) { heardRef.current = hasSpoken; setHeard(hasSpoken); }
      const prog = spokeEnough ? Math.min(1, (t - lastVoice) / hold) : 0;
      const q = Math.round(prog * 8) / 8; // quantize → at most 8 state updates
      if (q !== epRef.current) { epRef.current = q; setEndpointProgress(q); }
      if (spokeEnough && t - lastVoice > hold) { finishUtteranceRef.current(my, 'silence'); return; }
      if (t - started > MAX_UTTERANCE_MS) { finishUtteranceRef.current(my, hasSpoken ? 'max' : 'nospeech'); return; }
      if (!hasSpoken && t - started > NO_SPEECH_MS) { finishUtteranceRef.current(my, 'nospeech'); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    stopReasonRef.current = 'silence';
    try { rec.start(); } catch { setError('Voice capture failed to start.'); setPhaseBoth('error'); return; }
    setLevel(0);
    setPhaseBoth('listening');
    rafRef.current = requestAnimationFrame(tick);
  }, [clearWatchdog, cancelBargeRaf, transcribeAndSend, setPhaseBoth]);
  startListeningRef.current = startListening;

  // Acquire the mic + audio graph ONCE, then start the loop. Throws → error phase.
  const enter = useCallback(async () => {
    const my = ++runIdRef.current;
    // retry() (and any re-enter) can arrive while a prior graph is still live — an in-graph
    // error phase (429, recorder-start failure) leaves the mic/AudioContext open. Release it
    // first so we never overwrite streamRef/audioCtxRef and orphan a hot mic.
    releaseAudio();
    emptyListensRef.current = 0;
    mutedRef.current = false; setMuted(false); // a fresh session always starts unmuted
    setError(null);
    try {
      // AGC OFF: a fixed-threshold VAD needs STABLE gain — auto-gain pumps the noise floor up
      // toward the onset threshold (false clips, mis-ended turns) and invalidates calibration
      // frame-to-frame. Mono/16k halves the STT upload with no accuracy loss. Fall back to a
      // bare mic if a browser rejects the constraints.
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: STT_NOISE_SUPPRESSION, autoGainControl: false, channelCount: 1, sampleRate: 16000 },
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      if (my !== runIdRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;

      // P1A: continuous capture (AudioWorklet + pre-roll ring buffer). The engine runs for the
      // whole session and emits (a) onSpeechStart the instant you begin — so we duck/cancel
      // immediately in any phase — and (b) onUtterance (a WAV with the pre-roll prepended) at
      // end-of-turn. Falls back to the MediaRecorder + analyser path if the worklet is unavailable.
      if (CONTINUOUS_CAPTURE) {
        try {
          const cap = await createVoiceCapture(stream, {
            onLevel: (l) => setLevel(l),
            onHeard: (h) => { if (h !== heardRef.current) { heardRef.current = h; setHeard(h); } },
            onEndpointProgress: (p) => { if (p !== epRef.current) { epRef.current = p; setEndpointProgress(p); } },
            onSpeechStart: () => {
              if (!activeRef.current || mutedRef.current) return;
              const pp = phaseRef.current;
              if (pp === 'thinking' || pp === 'speaking') bargeCancelRef.current(runIdRef.current); // duck + abort the LLM
            },
            onUtterance: (wav) => {
              if (!activeRef.current || mutedRef.current) return;
              void transcribeAndSend(runIdRef.current, wav);
            },
          });
          if (my !== runIdRef.current) { try { cap.stop(); } catch { /* noop */ } stream.getTracks().forEach((t) => t.stop()); return; }
          captureRef.current = cap;
          continuousRef.current = true;
          setLevel(0);
          setPhaseBoth('listening');
          return;
        } catch {
          continuousRef.current = false; // worklet unavailable → MediaRecorder fallback
        }
      }

      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      try { await ctx.resume(); } catch { /* already running */ }
      if (my !== runIdRef.current) { try { void ctx.close(); } catch { /* noop */ } stream.getTracks().forEach((t) => t.stop()); return; }
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
      startListeningRef.current(my);
    } catch {
      if (my !== runIdRef.current) return;
      releaseAudio();
      setError('Microphone access is blocked. Allow mic access, then tap Retry.');
      setPhaseBoth('error');
      setLevel(0);
    }
  }, [releaseAudio, setPhaseBoth, transcribeAndSend]);

  // ── Reply capture: STREAM sentences into the speech queue as they generate ──
  // Fires on every commit where the reply text grows (lastAssistant is a committed prop, so
  // it's never stale). Each complete sentence is spoken the instant it's ready — the reply
  // starts playing ~1s in, not after the whole thing. On stream end we flush the tail and
  // seal the queue; when it drains, listening re-arms (queue.onDrained).
  useEffect(() => {
    streamingRef.current = streaming;
    if (!active) return;
    if (!expectReplyRef.current) return;          // not our turn's reply
    const p = phaseRef.current;
    if (p !== 'thinking' && p !== 'speaking') return;
    const content = lastAssistant?.content || '';
    // A5+B8: lock this turn's TTS affect once — from the reply content + the user's words (A5)
    // AND the prosody of how they just spoke (B8) — so every sentence shares one consistent
    // delivery that also mirrors the user's energy (apology stays gentle, good news stays bright).
    if (content && turnStyleRef.current === null) {
      turnStyleRef.current = buildDeliveryInstructions(
        content, lastUserTextRef.current, prosodyHint(speechPeakRef.current, pauseCountRef.current),
      );
    }
    const { sentences, consumed } = takeSentences(content, spokenUpToRef.current, !streaming, spokenUpToRef.current === 0);
    if (sentences.length) {
      for (const s of sentences) { const c = cleanForSpeech(s); if (c) speechQueueRef.current?.push(c); }
      spokenUpToRef.current = consumed;
    }
    if (!streaming) {                             // reply finished generating
      expectReplyRef.current = false;
      clearWatchdog();
      speechQueueRef.current?.seal();             // empty reply → drains immediately → re-listen
    }
  }, [streaming, active, lastAssistant, clearWatchdog]);

  // ── Barge-in / skip ──
  // CANCEL THE WHOLE TURN, not just playback: while a turn is in flight (thinking OR speaking)
  // the LLM may still be streaming, so we must abort it (onStop) AND clear expectReply — else
  // `streaming` stays true and transcribeAndSend discards your NEXT turn (the "I interrupt but
  // it ignores what I said" bug). Shared by manual interrupt + acoustic barge-in.
  const bargeCancel = useCallback((my: number) => {
    expectReplyRef.current = false;
    clearWatchdog();
    // B1: capture what actually PLAYED before tearing the queue down, then rewrite the stored
    // reply to just that — so next turn the model's history matches what the user heard, and it
    // won't reference (or repeat) the part it was cut off from. Runs after playback is already
    // cancelled → no sync risk; truncation is at whole-sentence granularity.
    const spoken = (speechQueueRef.current?.spokenText() || '').trim();
    speechQueueRef.current?.cancel();
    onStopRef.current?.();               // abort the in-flight LLM/agent turn → streaming clears
    if (spoken) onTruncateRef.current?.(spoken);
    startListeningRef.current(my);       // capture the interruption immediately
  }, [clearWatchdog]);
  bargeCancelRef.current = bargeCancel;

  const interrupt = useCallback(() => {
    if (!activeRef.current) return;
    const my = runIdRef.current;
    const p = phaseRef.current;
    if (p === 'thinking' || p === 'speaking') { bargeCancel(my); return; }
    if (p === 'listening') { finishUtteranceRef.current(my, 'nospeech'); return; }
  }, [bargeCancel]);

  // Mute/unmute the mic — hard-disables the audio track so it truly stops capturing (and
  // acoustic barge-in can't fire); the AI can still finish speaking. Unmute resumes the loop.
  const toggleMute = useCallback(() => {
    if (!activeRef.current) return;
    const my = runIdRef.current;
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    if (next) {
      // Muting while we've ALREADY heard you = "I'm done, answer now": SUBMIT the captured turn
      // so Breww replies (don't discard it), THEN disable the mic. We deliberately do NOT force
      // the 'muted' phase — the turn runs (transcribing → thinking → speaking); after the reply
      // drains, the loop lands on 'muted'. Also a reliable fallback if end-of-turn is finicky.
      if (phaseRef.current === 'listening' && heardRef.current) {
        if (continuousRef.current) captureRef.current?.submitNow();
        else finishUtteranceRef.current(my, 'silence');
        streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = false; });
        setLevel(0);
        return;
      }
      streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = false; });
      if (phaseRef.current === 'listening') { finishUtteranceRef.current(my, 'abort'); setPhaseBoth('muted'); setLevel(0); }
      else if (phaseRef.current === 'idle') setPhaseBoth('muted');
      // speaking/thinking/transcribing: let it finish; the loop lands on 'muted' next.
    } else {
      streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = true; });
      if (phaseRef.current === 'muted') startListeningRef.current(my); // resume listening
    }
  }, [setPhaseBoth]);

  // Tap-to-submit / "Done": end the current listen NOW and send it — the explicit "answer
  // now" escape hatch, so you never wait on the silence timer. Ignored if nothing was heard.
  const submitTurn = useCallback(() => {
    if (!activeRef.current) return;
    if (phaseRef.current !== 'listening' || !heardRef.current) return;
    if (continuousRef.current) { captureRef.current?.submitNow(); return; }
    finishUtteranceRef.current(runIdRef.current, 'silence');
  }, []);

  const retry = useCallback(() => { setError(null); void enter(); }, [enter]);

  // ── Lifecycle: enter on open; full teardown on close/unmount ──
  useEffect(() => {
    if (active) {
      void enter();
    } else {
      runIdRef.current++;
      clearWatchdog();
      abortRef.current?.abort();
      speechQueueRef.current?.cancel();
      speechQueueRef.current = null;
      expectReplyRef.current = false;
      emptyListensRef.current = 0;
      stopAllAudio();     // nuke ANY audio so nothing plays after the overlay closes
      releaseAudio();
      mutedRef.current = false; setMuted(false);
      setPhaseBoth('idle');
      setLevel(0);
      setTranscript('');
      setError(null);
    }
    return () => {
      runIdRef.current++;
      clearWatchdog();
      abortRef.current?.abort();
      speechQueueRef.current?.cancel();
      speechQueueRef.current = null;
      stopAllAudio();
      releaseAudio();
    };
    // enter/releaseAudio are stable; key only on `active`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { phase, transcript, level, error, muted, heard, endpointProgress, interrupt, submitTurn, toggleMute, retry };
}
